import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Home, FileText, LogOut, Menu, X, Tag, Info, Mail, Settings } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';

const Navigation = () => {
  const { user, isAuthenticated, logout } = useAuth();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isAdminMenuOpen, setIsAdminMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/');
    setIsMenuOpen(false);
  };

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
    setIsAdminMenuOpen(false);
  };

  const isActivePath = (path) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const NavLink = ({ to, icon: Icon, label }) => (
    <Link
      to={to}
      className={`flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium transition-all duration-300 ${isActivePath(to)
          ? 'text-church-green'
          : 'text-gray-300 hover:text-white hover:bg-white/5'
        }`}
    >
      <Icon className="h-4 w-4" />
      <span>{label}</span>
    </Link>
  );

  return (
    <nav className="sticky top-0 z-50 w-full border-b border-white/10 bg-black/80 backdrop-blur-md supports-[backdrop-filter]:bg-black/60">
      <div className="container flex h-20 max-w-screen-2xl items-center justify-between px-6">
        {/* Logo and Brand */}
        <Link
          to="/"
          className="flex items-center space-x-3 transition-opacity hover:opacity-80"
          onClick={closeMenu}
        >
          <div className="flex flex-col">
            <span className="text-2xl font-heading text-white tracking-wide">
              Igreja de Jardim
            </span>
            <span className="text-[10px] uppercase tracking-[0.2em] text-church-green font-sans font-semibold">
              Estudos Bíblicos
            </span>
          </div>
        </Link>

        {/* Desktop Navigation */}
        <div className="hidden md:flex items-center space-x-8">
          <nav className="flex items-center space-x-2">
            <NavLink to="/" icon={Home} label="Início" />
            <NavLink to="/sobre" icon={Info} label="Sobre" />
            <NavLink to="/contato" icon={Mail} label="Contato" />

            {/* Admin Menu - Only visible when authenticated */}
            {isAuthenticated && (
              <div className="relative ml-4">
                <button
                  onClick={() => setIsAdminMenuOpen(!isAdminMenuOpen)}
                  className={`flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium transition-all duration-300 ${isActivePath('/admin')
                      ? 'text-church-green'
                      : 'text-gray-300 hover:text-white hover:bg-white/5'
                    }`}
                >
                  <Settings className="h-4 w-4" />
                  <span>Admin</span>
                </button>

                {isAdminMenuOpen && (
                  <div className="absolute right-0 top-full mt-2 w-56 bg-church-night border border-white/10 rounded-lg shadow-xl py-2 z-50 animate-fade-in">
                    <div className="px-4 py-2 border-b border-white/10 mb-2">
                      <p className="text-xs text-church-gray">Logado como</p>
                      <p className="text-sm font-medium text-white truncate">{user?.name}</p>
                    </div>
                    <Link
                      to="/admin/posts"
                      onClick={() => setIsAdminMenuOpen(false)}
                      className="flex items-center px-4 py-2.5 text-sm text-gray-300 hover:text-white hover:bg-white/5"
                    >
                      <FileText className="inline h-4 w-4 mr-3" />
                      Gerenciar Posts
                    </Link>
                    <Link
                      to="/admin/posts/new"
                      onClick={() => setIsAdminMenuOpen(false)}
                      className="flex items-center px-4 py-2.5 text-sm text-gray-300 hover:text-white hover:bg-white/5"
                    >
                      <FileText className="inline h-4 w-4 mr-3" />
                      Novo Post
                    </Link>
                    <Link
                      to="/admin/tags"
                      onClick={() => setIsAdminMenuOpen(false)}
                      className="flex items-center px-4 py-2.5 text-sm text-gray-300 hover:text-white hover:bg-white/5"
                    >
                      <Tag className="inline h-4 w-4 mr-3" />
                      Gerenciar Tags
                    </Link>
                    <div className="my-1 border-t border-white/10" />
                    <button
                      onClick={handleLogout}
                      className="w-full text-left flex items-center px-4 py-2.5 text-sm text-red-400 hover:bg-red-500/10 hover:text-red-300"
                    >
                      <LogOut className="inline h-4 w-4 mr-3" />
                      Sair
                    </button>
                  </div>
                )}
              </div>
            )}
          </nav>
        </div>

        {/* Mobile Menu Button */}
        <div className="flex md:hidden items-center">
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleMenu}
            className="text-white hover:bg-white/10"
          >
            {isMenuOpen ? (
              <X className="h-6 w-6" />
            ) : (
              <Menu className="h-6 w-6" />
            )}
          </Button>
        </div>
      </div>

      {/* Mobile Navigation */}
      {isMenuOpen && (
        <div className="md:hidden border-t border-white/10 bg-black/95 backdrop-blur-xl absolute w-full left-0 animate-slide-down">
          <div className="container py-6 space-y-4 px-6">
            <Link
              to="/"
              className="flex items-center space-x-4 p-3 rounded-lg text-base font-medium text-gray-300 hover:text-white hover:bg-white/5"
              onClick={closeMenu}
            >
              <Home className="h-5 w-5" />
              <span>Início</span>
            </Link>

            <Link
              to="/sobre"
              className="flex items-center space-x-4 p-3 rounded-lg text-base font-medium text-gray-300 hover:text-white hover:bg-white/5"
              onClick={closeMenu}
            >
              <Info className="h-5 w-5" />
              <span>Sobre</span>
            </Link>

            <Link
              to="/contato"
              className="flex items-center space-x-4 p-3 rounded-lg text-base font-medium text-gray-300 hover:text-white hover:bg-white/5"
              onClick={closeMenu}
            >
              <Mail className="h-5 w-5" />
              <span>Contato</span>
            </Link>

            {/* Admin section for mobile */}
            {isAuthenticated && (
              <div className="pt-4 border-t border-white/10">
                <div className="text-xs uppercase tracking-wider text-church-gray mb-3 px-3">Administração</div>

                <Link
                  to="/admin/posts"
                  className="flex items-center space-x-4 p-3 rounded-lg text-base font-medium text-gray-300 hover:text-white hover:bg-white/5"
                  onClick={closeMenu}
                >
                  <FileText className="h-5 w-5" />
                  <span>Gerenciar Posts</span>
                </Link>

                <Link
                  to="/admin/posts/new"
                  className="flex items-center space-x-4 p-3 rounded-lg text-base font-medium text-gray-300 hover:text-white hover:bg-white/5"
                  onClick={closeMenu}
                >
                  <FileText className="h-5 w-5" />
                  <span>Novo Post</span>
                </Link>

                <Link
                  to="/admin/tags"
                  className="flex items-center space-x-4 p-3 rounded-lg text-base font-medium text-gray-300 hover:text-white hover:bg-white/5"
                  onClick={closeMenu}
                >
                  <Tag className="h-5 w-5" />
                  <span>Gerenciar Tags</span>
                </Link>

                <button
                  onClick={handleLogout}
                  className="w-full flex items-center space-x-4 p-3 rounded-lg text-base font-medium text-red-400 hover:bg-red-500/10"
                >
                  <LogOut className="h-5 w-5" />
                  <span>Sair</span>
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navigation;